import type { FieldValues } from 'react-hook-form';
import type {
  SelectOption,
  SelectOptionDefault,
  SelectOptionsSource,
} from '../config/types';
import type { FormAssets } from '../service/types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toSelectOptions = (value: unknown): SelectOption[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const resolveAnswerValue = (answers: FieldValues | undefined, questionName: string): string => {
  if (!answers || !questionName) return '';

  const direct = answers[questionName];
  if (typeof direct === 'string' && direct.trim()) {
    return direct;
  }

  const matchedKey = Object.keys(answers).find((key) => {
    return key === questionName || key.endsWith(`-${questionName}`) || key.endsWith(questionName);
  });

  if (!matchedKey) return '';
  const matchedValue = answers[matchedKey];
  return typeof matchedValue === 'string' ? matchedValue : '';
};

const resolveMappedChildren = (
  assetValue: UnknownRecord,
  answers: FieldValues | undefined,
): SelectOption[] => {
  const parentQuestionName =
    typeof assetValue.parentQuestionName === 'string'
      ? assetValue.parentQuestionName
      : typeof assetValue.parentQuestion === 'string'
        ? assetValue.parentQuestion
        : '';

  if (!parentQuestionName || !isRecord(assetValue.childrenByParent)) return [];

  const parentAnswer = resolveAnswerValue(answers, parentQuestionName);
  if (!parentAnswer) return [];

  return toSelectOptions(assetValue.childrenByParent[parentAnswer]);
};

const resolveMappedParents = (assetValue: UnknownRecord): SelectOption[] => {
  const parentsFromList = toSelectOptions(assetValue.parents);
  if (parentsFromList.length > 0) return parentsFromList;

  if (!isRecord(assetValue.childrenByParent)) return [];
  return toSelectOptions(Object.keys(assetValue.childrenByParent));
};

export const resolveSelectOptions = ({
  options,
  questionName,
  assets,
  answers,
}: {
  options: SelectOptionsSource | undefined;
  questionName: string;
  assets?: FormAssets;
  answers?: FieldValues;
}): SelectOption[] => {
  if (Array.isArray(options)) return options;
  if (options !== 'assets') return [];

  const assetValue = assets?.select?.[questionName];
  if (!assetValue) return [];

  if (Array.isArray(assetValue)) {
    return toSelectOptions(assetValue);
  }

  if (!isRecord(assetValue)) {
    return [];
  }

  if (assetValue.mode === 'single') {
    return toSelectOptions(assetValue.options);
  }

  if (assetValue.mode === 'mapped') {
    const parentQuestionName =
      typeof assetValue.parentQuestionName === 'string' ? assetValue.parentQuestionName : '';
    if (parentQuestionName && parentQuestionName === questionName) {
      return resolveMappedParents(assetValue);
    }

    if (parentQuestionName) {
      return resolveMappedChildren(assetValue, answers);
    }

    const parents = resolveMappedParents(assetValue);
    if (parents.length > 0) return parents;
    return resolveMappedChildren(assetValue, answers);
  }

  const explicitOptions = toSelectOptions(assetValue.options);
  if (explicitOptions.length > 0) return explicitOptions;

  const parentOptions = toSelectOptions(assetValue.parents);
  if (parentOptions.length > 0) return parentOptions;

  return resolveMappedChildren(assetValue, answers);
};

export const getSelectableOptionValues = (options: SelectOption[]): string[] => {
  return options.flatMap((option) => {
    if (typeof option === 'string') return [option];
    if ('label' in option && 'options' in option) return option.options;
    if (option.retired) return [];
    return [getSelectOptionValue(option)];
  });
};

export const getSingleSelectableOptionValue = (
  options: SelectOption[],
): string | undefined => {
  const values = getSelectableOptionValues(options);
  return values.length === 1 ? values[0] : undefined;
};

export const getSelectOptionValue = (option: SelectOptionDefault): string =>
  String(option.value ?? option.option);

export const getSelectOptionLabel = (option: SelectOptionDefault): string =>
  option.option;

export const getDisplayLabelForStoredSelectValue = (
  options: SelectOptionsSource | undefined,
  storedValue: unknown,
): string | undefined => {
  if (!Array.isArray(options)) return undefined;

  const normalizedStoredValue = String(storedValue ?? '').trim();
  if (!normalizedStoredValue) return undefined;

  for (const option of options) {
    if (typeof option === 'string') {
      if (option === normalizedStoredValue) return option;
      continue;
    }

    if ('label' in option && 'options' in option) {
      const matchedGroupOption = option.options.find((entry) => entry === normalizedStoredValue);
      if (matchedGroupOption) return matchedGroupOption;
      continue;
    }

    if (getSelectOptionValue(option) === normalizedStoredValue) {
      return getSelectOptionLabel(option);
    }
  }

  return undefined;
};
